import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import { infoScreenDisplayType, locationType } from '../../../../../enums';
import * as models from '../../../../../models';
import { mapLocationsToOptions } from '../../../../../utils/mappers';

interface IInfoScreenEditModalProps {
    infoScreenId: number;
    refetchInfoScreens(): Promise<void>;
}

const validationSchema = Yup.object<models.IInfoScreenListItem>().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    model: Yup.string().required('Šis laukas yra privalomas'),
    displayType: Yup.number().required('Šis laukas yra privalomas')
});

const InfoScreenEditModalWindow: FC<IWindowComponentProps<IInfoScreenEditModalProps>> = (props) => {
    const {
        componentProps: { infoScreenId, refetchInfoScreens },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [infoScreen, setInfoScreen] = useState<models.IInfoScreenListItem | null>(null);
    const [floorTypeLocations, setFloorTypeLocations] = useState<ILocationItem[] | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [infoScreenResp, floorTypeLocationsResp] = await Promise.all([
                api.administration.getInfoScreen(infoScreenId),
                api.administration.getLocations(locationType.Level)
            ]);

            setInfoScreen(infoScreenResp);
            setFloorTypeLocations(floorTypeLocationsResp);
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: models.IInfoScreenListItem, formikActions: formik.FormikHelpers<models.IInfoScreenListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.administration.updateInfoScreen(values);
            toast.success('Įrenginio informacija sėkmingai išsaugota');

            formikActions.resetForm();
            await fetchData();
            await refetchInfoScreens();
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
            onClose();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {infoScreen && (
                        <formik.Formik
                            validationSchema={validationSchema}
                            initialValues={{ ...infoScreen }}
                            onSubmit={handleSubmit}
                            render={(infoScreen) => (
                                <formik.Form>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="name"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Pavadinimas"
                                                        placeholder="Įrašykite info ekrano pavadinimą"
                                                        errorMessage={
                                                            (infoScreen.status && infoScreen.status.name) || (form.errors.name && form.errors.name)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="model"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        label="Modelis"
                                                        placeholder="Įrašykite info ekrano modelį"
                                                        errorMessage={
                                                            (infoScreen.status && infoScreen.status.model) || (form.errors.model && form.errors.model)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="displayType"
                                                render={({ field, form }: formik.FieldProps<enums.InfoScreenDisplayType>) => (
                                                    <fabric.Dropdown
                                                        disabled
                                                        selectedKey={field.value}
                                                        errorMessage={
                                                            (infoScreen.status && infoScreen.status.displayType) ||
                                                            (form.errors.displayType && form.errors.displayType)
                                                        }
                                                        options={enums.infoScreenDisplayType.displayTypesArray()}
                                                        onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                        defaultSelectedKey={form.values.displayType}
                                                        label="Atvaizdavimo tipas"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="screenSoundType"
                                                render={({ field, form }: formik.FieldProps<enums.ScreenSoundType>) => (
                                                    <fabric.Dropdown
                                                        defaultSelectedKey={field.value}
                                                        {...field}
                                                        onChange={({}, option?: fabric.IDropdownOption) =>
                                                            option && form.setFieldValue(field.name, option.key)
                                                        }
                                                        label="Skambėjimo tipas"
                                                        options={enums.screenSoundType.displayTypesArray()}
                                                        errorMessage={
                                                            (infoScreen.status && infoScreen.status.screenSoundType) ||
                                                            (form.errors.screenSoundType && form.errors.screenSoundType)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="locationId"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        options={mapLocationsToOptions(floorTypeLocations, true)}
                                                        defaultSelectedKey={field.value}
                                                        {...field}
                                                        onChange={({}, value: any) =>
                                                            value.key === 'null'
                                                                ? form.setFieldValue(field.name, null)
                                                                : form.setFieldValue(field.name, value.key)
                                                        }
                                                        label="Aukštas"
                                                        errorMessage={
                                                            (infoScreen.status && infoScreen.status.locationId) ||
                                                            (form.errors.locationId && form.errors.locationId)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <fabric.Label style={{ marginBottom: '5px' }}>Nuoroda į įrenginio rodomą vaizdą</fabric.Label>
                                            <fabric.Link target="_blank" href={`${window.location.origin}/display/infoScreen/${infoScreenId}`}>
                                                <fabric.Icon style={{ fontSize: '30px' }} iconName="PageArrowRight" />
                                            </fabric.Link>
                                        </div>
                                    </div>
                                    {infoScreen.values['displayType'] === infoScreenDisplayType.Schedule && (
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <formik.Field
                                                    name="workScheduleRowsDisplayType"
                                                    render={({ field, form }: formik.FieldProps<enums.ScreenSoundType>) => (
                                                        <fabric.Dropdown
                                                            defaultSelectedKey={field.value}
                                                            {...field}
                                                            onChange={({}, option?: fabric.IDropdownOption) =>
                                                                option && form.setFieldValue(field.name, option.key)
                                                            }
                                                            label="Tvarkarščio ekrano tipas"
                                                            options={enums.workScheduleRowsDisplayType.displayTypesArray()}
                                                            errorMessage={
                                                                (infoScreen.status && infoScreen.status.workScheduleRowsDisplayType) ||
                                                                (form.errors.workScheduleRowsDisplayType && form.errors.workScheduleRowsDisplayType)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                        <fabric.PrimaryButton
                                            className="ms-slideRightIn40 ms-textAlignRight"
                                            text="Išsaugoti"
                                            disabled={infoScreen.isSubmitting || !infoScreen.isValid || !infoScreen.dirty}
                                            type="submit"
                                        />
                                    </div>
                                </formik.Form>
                            )}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default InfoScreenEditModalWindow;
